import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { AlarmClock } from "lucide-react";
import { Pill, KIND_STYLES } from "@/lib/inquiry";
import { fmtDate } from "@/lib/format";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const bucket = (d, today, week) => (d < today ? "overdue" : d === today ? "today" : d <= week ? "week" : "later");
const GROUPS = [
  { key: "overdue", label: "Overdue", tone: "text-rose-700" },
  { key: "today", label: "Due today", tone: "text-amber-700" },
  { key: "week", label: "Next 7 days", tone: "text-blue-700" },
  { key: "later", label: "Later", tone: "text-slate-600" },
];

const Item = ({ r, onOpen }) => (
  <button type="button" data-testid={`fu-row-${r.id}`} onClick={onOpen} className="w-full text-left flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-blue-50/40">
    <span className="mono text-xs text-blue-700 w-28">{r.number}</span>
    <span className="flex-1 min-w-[200px]"><span className="font-medium text-sm">{r.subject}</span>
      <span className="block text-xs text-slate-500">{r.next_action || "Follow up"} · {r.organisation || r.contacts?.[0]?.name || "—"}</span></span>
    <Pill className={KIND_STYLES[r.status_kind]}>{r.status_label}</Pill>
    <span className="text-xs text-slate-600 w-28">{r.assigned_name}</span>
    <span className="mono text-xs w-24 text-right">{fmtDate(r.next_followup_date)}</span>
  </button>
);

export default function InquiryFollowups() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/inquiries?state=open").then((r) => setRows(r.data.filter((x) => x.next_followup_date))).catch((e) => toast.error(formatErr(e))); }, []);
  const today = iso(new Date());
  const week = iso(new Date(Date.now() + 7 * 864e5));
  const grouped = Object.fromEntries(GROUPS.map((g) => [g.key, []]));
  (rows || []).sort((a, b) => a.next_followup_date.localeCompare(b.next_followup_date)).forEach((r) => grouped[bucket(r.next_followup_date, today, week)].push(r));
  return (
    <div data-testid="followups-page">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">Inquiries</div>
      <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6 flex items-center gap-2"><AlarmClock className="w-7 h-7 text-blue-700" />Follow-ups</h1>
      {rows && rows.length === 0 && <div className="text-sm text-slate-500" data-testid="fu-empty">No follow-ups scheduled on open inquiries.</div>}
      <div className="space-y-5">
        {GROUPS.filter((g) => grouped[g.key].length).map((g) => (
          <section key={g.key} className="bg-white border border-slate-200 rounded-md" data-testid={`fu-group-${g.key}`}>
            <div className={`px-4 py-2 border-b border-slate-100 text-xs font-semibold uppercase tracking-wider ${g.tone}`}>{g.label} · {grouped[g.key].length}</div>
            {grouped[g.key].map((r) => <Item key={r.id} r={r} onOpen={() => nav(`/inquiries/${r.id}`)} />)}
          </section>
        ))}
      </div>
    </div>
  );
}
